import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  Injector,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  ViewChild
} from '@angular/core';
import {
  CardTypeEnum,
  ChartFilterPatternNameEnum,
  ChartTypeEnum,
  CustomControlsNameEnum,
  IChartFilter,
  IChartFilterPattern,
  ISelectedFilters,
  RealTimeEnum,
  TimeRange,
  TimeRangeEnum
} from 'app/charts-dashboard/interfaces';
import {
  FormControl,
  FormGroup
} from '@angular/forms';
import {
  debounceTime,
  distinctUntilChanged,
  takeUntil
} from 'rxjs/operators';
import { ChartsCommonService } from 'app/charts-dashboard/services/charts-common.service';
import { locale } from 'app/charts-dashboard/components/card-chart/controls/locale';
import { AppStore } from '@iv/store/app.store';
import * as Redux from 'redux';
import { AppState } from '@iv/store/app.reducer';
import { CardChartActionsComponent } from 'app/charts-dashboard/components/card-chart/actions/card-chart-actions.component';
import {
  isIntraday,
  realtimeWorkingDays
} from '../../../helpers/charts-module-helpers';
import { ChartsDashboardStateService } from 'app/charts-dashboard/state/charts-dashboard-state.service';
import { ChartsDashboardQuery } from 'app/charts-dashboard/state/dashboard';
import { ChartsDialogsService } from 'app/charts-dashboard/services/charts-dialogs.service';
import { ChartsDashboardSeriesQuery } from 'app/charts-dashboard/state/series';
import {
  EntityActions,
  ID
} from '@datorama/akita';
import { cleanObjectFromUndefinedProperties } from '@iv/shared/helpers/common.helper';
import * as moment from 'moment';
import { StockTypeEnum } from '@iv/model/instrument';

@Component({
  selector: 'iv-live-card-chart-controls',
  templateUrl: './card-chart-controls.component.html',
  styleUrls: ['./card-chart-controls.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CardChartControlsComponent extends CardChartActionsComponent implements OnInit, AfterViewInit, OnDestroy {

  constructor(
    protected readonly chartsCommonService: ChartsCommonService,
    protected readonly chartsState: ChartsDashboardStateService,
    @Inject(AppStore) protected store: Redux.Store<AppState>,
    @Inject(Injector) protected readonly injector: Injector,
    protected readonly chartsDialogsService: ChartsDialogsService,
    protected readonly seriesQuery: ChartsDashboardSeriesQuery,
    private readonly dashboardQuery: ChartsDashboardQuery,
    private readonly renderer2: Renderer2,
    private readonly changeDetectorRef: ChangeDetectorRef
  ) {
    super(
      chartsCommonService,
      store,
      injector,
      chartsDialogsService,
      chartsState,
      seriesQuery
    );
  }

  @ViewChild('chartNameSpan')
  private chartNameContainer: ElementRef;

  @ViewChild('chartControls')
  private chartControlsContainer: ElementRef;

  @ViewChild('burgerMenuControls')
  private burgerMenuControlsContainer: ElementRef;

  @Output()
  private skipSettingTimestamp = new EventEmitter<boolean>();

  public locale = locale;
  public form = new FormGroup({});
  public isNeedBurgerMenu: boolean;
  public isBurgerMenuClosed: boolean;

  private get d1(): IChartFilterPattern<TimeRange> {
    return this.getChartFilter(ChartFilterPatternNameEnum.TIME_RANGE).pattern
      .find((value: IChartFilterPattern<TimeRange>) => value.name === TimeRangeEnum.D1) as IChartFilterPattern<TimeRange>;
  }

  public get chartName(): string {
    return this.chartsCommonService.getChartName(this.card.chartId);
  }

  public ngOnInit(): void {
    this.dashboardSubscription();
    this.configForm();
    this.valueChanges();
    this.seriesSubscription();
  }

  public ngAfterViewInit() {
    this.recalculateControlContainer();
  }

  public dateIsEod(flag: boolean): void {
    const realtime = this.form.get('realtime');

    if (flag) {
      realtime.setValue({name: RealTimeEnum.OFF, description: 'Off'}, {emitEvent: false});
      realtime.disable();
    } else {
      realtime.enable();
    }
  }

  public deleteChartLayout(cardId: string): void {
    this.chartsState.deleteChartArea(cardId);
  }

  public toggleBurgerMenu(): void {
    this.isBurgerMenuClosed = !this.isBurgerMenuClosed;
  }

  public toggleRtMode(): void {
    const {realtime, timeRange, datetime} = this.form.controls;

    if (realtime.disabled) return;

    realtime.setValue(
      realtime.value.name === RealTimeEnum.ON ?
        {name: RealTimeEnum.OFF, description: 'Off'} :
        {name: RealTimeEnum.ON, description: 'On'},
      {emitEvent: false}
    );

    if (this.chartsCommonService.isHistorical(this.card.chartId) && !isIntraday(timeRange.value.name)) {
      timeRange.setValue(this.d1, {emitEvent: false});
    }

    if (this.chartsCommonService.isMaturity(this.card.chartId)) {
      realtime.value.name === RealTimeEnum.ON
        ? this.form.get(ChartFilterPatternNameEnum.DATE_TIME).disable()
        : this.form.get(ChartFilterPatternNameEnum.DATE_TIME).enable();
    }

    this.form.setValue(this.chartsCommonService.isMaturity(this.card.chartId) ? {
      ...this.form.value,
      realtime: realtime.value,
      datetime: datetime?.value
    } : {
      ...this.form.value,
      realtime: realtime.value,
      timeRange: timeRange.value
    });
  }

  public onVMode(): void {
    const timeRange: IChartFilterPattern<TimeRange> = this.form.get(ChartFilterPatternNameEnum.TIME_RANGE).value;
    if (!isIntraday(timeRange.name) && !this.form.get(CustomControlsNameEnum.V_MODE_TOGGLER).value) {
      this.form.get(ChartFilterPatternNameEnum.TIME_RANGE).setValue(this.d1, {emitEvent: false});
    }

    const selectedFilters: ISelectedFilters = this.form.value;

    this.chartsState.updateSelectedFilters(this.card, this.mainSeries.id, selectedFilters);
  }

  // TODO: Процентный режим временно скрыт
  // public toggleRelative(): void {
  //   this.form.get('relativeMode').setValue(!this.form.get('relativeMode').value, { emitEvent: false });
  //   this.relativeModeChanges.emit(this.form.get('relativeMode').value); // Устарело, поменять на akita state
  // }

  public toggleFullScreen(): void {
    this.isFullScreen = !this.isFullScreen;
    this.chartsState.toggleFullScreen(this.card.id, this.isFullScreen);
    this.recalculateControlContainer();
  }

  public takeScreenshot(): void {
    this.chartsCommonService.$takeScreenshot.next(this.card.id);
  }

  @HostListener('document:fullscreenchange', ['$event'])
  @HostListener('document:webkitfullscreenchange', ['$event'])
  @HostListener('document:mozfullscreenchange', ['$event'])
  @HostListener('document:MSFullscreenChange', ['$event'])
  private fullScreenChange(event: Event): void {
    if (event && !document.fullscreenElement) {
      this.isFullScreen = false;
      // this.chartsCommonService.$isUpdated.next();
    }
  }

  @HostListener('window:resize')
  public resize() {
    this.calcSizeControlContainerOnce();
  }

  private recalculateControlContainer(): void {
    for (let i = 0; i < Object.keys(this.mainSeries.selectedFilters).length + 2; i++) {
      this.calcSizeControlContainerOnce();
    }
  }

  private calcSizeControlContainerOnce(): void {
    if (this.card.type !== CardTypeEnum.FILLED) return;

    this.changeDetectorRef.detectChanges();

    const chartNameContainerRect: DOMRect = this.chartNameContainer.nativeElement.getBoundingClientRect();
    const chartControlsContainerRect: DOMRect = this.chartControlsContainer.nativeElement.getBoundingClientRect();
    const isSmall = chartNameContainerRect.x + chartNameContainerRect.width + 35 >= chartControlsContainerRect.x;
    const isLarge = chartNameContainerRect.x + chartNameContainerRect.width + 200 <= chartControlsContainerRect.x;

    if (isSmall) {
      this.addElementToBurgerMenu(this.chartControlsContainer.nativeElement.children);
      return;
    }

    if (isLarge) {
      this.deleteElementFromBurgerMenu(this.burgerMenuControlsContainer?.nativeElement);
    }
  }

  private addElementToBurgerMenu(chartControlsContainerCollection: HTMLCollection): void {
    if (!chartControlsContainerCollection.length) return;

    if (!this.isNeedBurgerMenu) {
      this.isNeedBurgerMenu = true;
      this.isBurgerMenuClosed = true;
      this.changeDetectorRef.detectChanges();
    }

    const lastItem = chartControlsContainerCollection[chartControlsContainerCollection.length - 1];
    const itemContainer = this.renderer2.createElement('div');

    this.renderer2.addClass(itemContainer, 'burger-menu-item');
    this.renderer2.appendChild(this.burgerMenuControlsContainer.nativeElement, itemContainer);
    this.renderer2.appendChild(itemContainer, lastItem);

    if (
      lastItem.className.includes('controls__full-screen') ||
      lastItem.className.includes('controls__auto-update') ||
      lastItem.className.includes('controls__relative') ||
      lastItem.className.includes('controls__screenshot')
    ) {
      const itemDescription = this.renderer2.createElement('span');

      this.renderer2.addClass(itemDescription, 'description');
      this.renderer2.appendChild(itemContainer, itemDescription);
      this.renderer2.setProperty(itemDescription, 'innerHTML', lastItem.children[lastItem.children.length - 1].innerHTML);
    }
  }

  private deleteElementFromBurgerMenu(burgerMenuControlsContainerElement: HTMLElement): void {
    if (!burgerMenuControlsContainerElement?.children.length) return;

    const lastElement = burgerMenuControlsContainerElement.children[burgerMenuControlsContainerElement.children.length - 1];

    this.renderer2.appendChild(this.chartControlsContainer.nativeElement, lastElement.children[0]);
    this.renderer2.removeChild(burgerMenuControlsContainerElement, lastElement);

    if (this.burgerMenuControlsContainer.nativeElement.children[0].children.length === 1) {
      this.isNeedBurgerMenu = false;
      this.changeDetectorRef.detectChanges();
    }
  }

  private valueChanges(): void {
    const realtime = this.form.get(ChartFilterPatternNameEnum.REALTIME);

    this.form.valueChanges
      .pipe(
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
        takeUntil(this.destroy$)
      )
      .subscribe((value: ISelectedFilters) => {
        const selectedFilters: ISelectedFilters = {};

        for (const key of Object.keys(value)) {
          selectedFilters[key] = value[key];
        }

        if (
          realtime?.value &&
          !isIntraday(value.timeRange?.name) &&
          this.chartsCommonService.isHistorical(this.card.chartId)
        ) {
          if (this.card.chartId === ChartTypeEnum.SPOT_CHART && value.vModeToggler) {
            this.form.get(CustomControlsNameEnum.V_MODE_TOGGLER).setValue(false, {emitEvent: false});
            selectedFilters[CustomControlsNameEnum.V_MODE_TOGGLER] = false;
          }
          realtime.setValue({name: RealTimeEnum.OFF, description: 'Off'}, {emitEvent: false});
          selectedFilters[ChartFilterPatternNameEnum.REALTIME] = realtime.value;
        }

        if (this.chartsCommonService.isMaturity(this.card.chartId)) {
          this.skipSettingTimestamp.emit(
            selectedFilters[ChartFilterPatternNameEnum.DATE_TIME] === this.seriesQuery.getEntity(this.mainSeries.id).selectedFilters.datetime
          );
        }

        this.chartsState.updateSelectedFilters(this.card, this.mainSeries.id, selectedFilters);
      });
  }

  private configForm(): void {
    this.card.filters.forEach((filter: IChartFilter) => {
      if (filter.parameterId === ChartFilterPatternNameEnum.STOCK_ID) return;

      this.form.addControl(filter.parameterId, new FormControl(this.mainSeries.selectedFilters[filter.parameterId]));

      if (
        this.chartsCommonService.isHistorical(this.card.chartId) &&
        filter.parameterId === ChartFilterPatternNameEnum.REALTIME &&
        !this.mainSeries.selectedFilters[ChartFilterPatternNameEnum.REALTIME]
      ) {
        this.form.get(ChartFilterPatternNameEnum.REALTIME).setValue({name: RealTimeEnum.ON, description: 'On'});
      }
    });

    switch (this.card.chartId) {
      case ChartTypeEnum.SPOT_CHART:
        this.form.addControl(CustomControlsNameEnum.V_MODE_TOGGLER, new FormControl(
          !!this.mainSeries.selectedFilters[CustomControlsNameEnum.V_MODE_TOGGLER]
        ));

        if (
          this.mainSeries.stock.stockType === StockTypeEnum.FX_INDEX ||
          this.mainSeries.stock.stockType === StockTypeEnum.INDEX
        ) {
          this.form.get(CustomControlsNameEnum.V_MODE_TOGGLER).setValue(false);
          this.form.get(CustomControlsNameEnum.V_MODE_TOGGLER).disable();
        }
        break;
      case ChartTypeEnum.TERM_STRUCTURE_ROLLING:
      case ChartTypeEnum.SKEW_CHART:
        if (this.form.get(ChartFilterPatternNameEnum.REALTIME).value.name === RealTimeEnum.ON) {
          this.form.addControl(ChartFilterPatternNameEnum.DATE_TIME, new FormControl(null));
          this.form.get(ChartFilterPatternNameEnum.DATE_TIME).disable();
        }
        break;
    }

    if (this.chartsCommonService.isMaturity(this.card.chartId)) {
      if (moment(this.mainSeries.selectedFilters.datetime).unix() * 1000 < realtimeWorkingDays) {
        this.form.get(ChartFilterPatternNameEnum.REALTIME).setValue({name: RealTimeEnum.OFF, description: 'Off'});
        this.form.get(ChartFilterPatternNameEnum.REALTIME).disable();
      }
    }

    // TODO: Процентный режим временно скрыт
    // this.form.addControl(SystemControlsNameEnum.RELATIVE_MODE, new FormControl(false));

    // if (this.card.chartLayout.compareSeries.length) {
    //   this.toggleRelative();
    // }
  }

  private seriesSubscription(): void {
    this.seriesQuery.selectEntityAction(EntityActions.Update)
      .pipe(takeUntil(this.destroy$))
      .subscribe((ids: ID[]) => {
        if (ids.filter(id => this.card.chartSeries.includes(id)).length) {
          let selectedFilters: ISelectedFilters;

          if (this.mainSeries.selectedFilters.hasOwnProperty('vModeToggler') && this.mainSeries.selectedFilters.vModeToggler === undefined) {
            selectedFilters = {...this.mainSeries.selectedFilters, vModeToggler: false};
          } else {
            selectedFilters = this.mainSeries.selectedFilters;
          }

          this.form.setValue(cleanObjectFromUndefinedProperties(selectedFilters), {emitEvent: false});

          if (this.card.chartId === ChartTypeEnum.SPOT_CHART) {
            if (this.mainSeries.stock.stockType === StockTypeEnum.FX_INDEX || this.mainSeries.stock.stockType === StockTypeEnum.INDEX) {
              this.form.get(CustomControlsNameEnum.V_MODE_TOGGLER).setValue(false);
              this.form.get(CustomControlsNameEnum.V_MODE_TOGGLER).disable();
            } else {
              this.form.get(CustomControlsNameEnum.V_MODE_TOGGLER).enable();
            }
          }

          if (this.isMaturity && this.mainSeries.selectedFilters.realtime.name === RealTimeEnum.ON) {
            this.form.get(ChartFilterPatternNameEnum.DATE_TIME).disable();
            this.changeDetectorRef.detectChanges();
          }
        }
      });
  }

  private dashboardSubscription(): void {
    this.dashboardQuery.selectDashboard(this.dashboardQuery.getCurrentDashboardId)
      .pipe(
        debounceTime(1),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.recalculateControlContainer();
        this.changeDetectorRef.detectChanges();
      });
  }
}
